import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { API } from '@/lib/api';

const emptyForm = () => ({
  doctor_id: '',
  fecha: new Date().toISOString().split('T')[0],
  hora_inicio: '09:00',
  motivo: '',
  notas: '',
});

/**
 * Diálogo para agendar una cita desde el expediente del paciente.
 * Carga la lista de doctores al abrirse y notifica con onCreated al guardar.
 */
export const AgendarCitaDialog = ({ patientId, patient, open, onOpenChange, onCreated }) => {
  const [doctors, setDoctors] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm());
    if (doctors.length === 0) {
      axios.get(`${API}/doctors`)
        .then(res => setDoctors(res.data))
        .catch(() => toast.error('No se pudo cargar la lista de doctores'));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.doctor_id || !form.fecha || !form.motivo) {
      toast.error('Doctor, fecha y motivo son obligatorios');
      return;
    }
    setSaving(true);
    try {
      const [h, m] = form.hora_inicio.split(':');
      const totalMin = parseInt(h) * 60 + parseInt(m) + 30;
      const hora_fin = `${String(Math.floor(totalMin / 60)).padStart(2, '0')}:${String(totalMin % 60).padStart(2, '0')}`;
      await axios.post(`${API}/appointments`, {
        paciente_id: patientId,
        doctor_id:   form.doctor_id,
        fecha:       form.fecha,
        hora_inicio: form.hora_inicio,
        hora_fin,
        motivo:      form.motivo,
        notas:       form.notas || null,
        estado:      'citado',
      });
      toast.success('Cita agendada');
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al agendar cita');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Agendar cita — {patient?.nombre} {patient?.apellido}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div className="space-y-1">
            <label className="text-sm font-medium">Doctor *</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.doctor_id}
              onChange={e => setForm({ ...form, doctor_id: e.target.value })}
              required
            >
              <option value="">Seleccionar doctor...</option>
              {doctors.map(d => (
                <option key={d.id} value={d.id}>{d.nombre} {d.apellido}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Fecha *</label>
              <input
                type="date"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.fecha}
                min={new Date().toISOString().split('T')[0]}
                onChange={e => setForm({ ...form, fecha: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Hora *</label>
              <input
                type="time"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.hora_inicio}
                onChange={e => setForm({ ...form, hora_inicio: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Motivo *</label>
            <input
              type="text"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Consulta general, revisión..."
              value={form.motivo}
              onChange={e => setForm({ ...form, motivo: e.target.value })}
              required
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Notas</label>
            <textarea
              rows={2}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
              placeholder="Observaciones opcionales..."
              value={form.notas}
              onChange={e => setForm({ ...form, notas: e.target.value })}
            />
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" className="flex-1" disabled={saving}>
              {saving ? 'Guardando...' : 'Agendar cita'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
